import { useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendarPlus,
  IconCompass,
  IconDownload,
  IconLock,
  IconRefresh,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useStaffProfile } from '../profile-context.tsx';
import { useDashboard } from '../hooks/useDashboard.ts';
import { useReservationActions } from '../hooks/useReservationActions.ts';
import { ReservationCard } from '../components/ReservationCard.tsx';
import { SeatMap } from '../components/SeatMap.tsx';
import { NewReservationModal } from '../components/NewReservationModal.tsx';
import { AllocateSeatsModal } from '../components/AllocateSeatsModal.tsx';
import { TodayStatsHero } from '../components/TodayStatsHero.tsx';
import { TourModal } from '../components/TourModal.tsx';
import { TakeoutPanel } from '../components/TakeoutPanel.tsx';
import { NowPlayingPanel } from '../components/NowPlayingPanel.tsx';
import { TimelineView } from '../components/TimelineView.tsx';
import { ZonePricingModal } from '../components/ZonePricingModal.tsx';
import { ServicePolicyModal } from '../components/ServicePolicyModal.tsx';
import { HelpPopover } from '../components/HelpPopover.tsx';
import { PAGE_HELP } from '../help/page-descriptions.tsx';
import { useFirstVisitFlag } from '../hooks/useFirstVisitFlag.ts';
import { ZoneMetadataModal } from '../components/ZoneMetadataModal.tsx';
import { ZoneUnitsModal } from '../components/ZoneUnitsModal.tsx';
import { tokens } from '../ui/tokens.ts';
import { rowsToCsv, downloadCsv } from '../utils/csv-export.ts';
import type { DashboardRow, Zone } from '../api-client.ts';

const CARD_EMOJI = ['🌸', '🍰', '🦋', '🌼', '🍓', '🌈', '🎀', '🧁', '🎮', '🌻', '💫', '🪩'];
const WINDOW_HOURS = 24;

export function DashboardPage(): JSX.Element {
  const profile = useStaffProfile();
  const [now] = useState(new Date());
  const windowStart = useMemo(() => now.toISOString(), [now]);
  const windowEnd = useMemo(
    () => new Date(now.getTime() + WINDOW_HOURS * 60 * 60 * 1000).toISOString(),
    [now],
  );

  const { rows, zones, loading, error, reload } = useDashboard({
    venueId: profile.venueId,
    todayIsoDate: windowStart,
    timezone: profile.timezone,
    windowStartIso: windowStart,
    windowEndIso: windowEnd,
  });

  const zoneSummaries = useMemo(() => zones.map((z) => z.zone), [zones]);

  // Track seen reservation IDs so polling can emit a toast when a NEW
  // one appears (客人剛下單，staff 不用盯著清單也知道)。First render seeds
  // the set and doesn't toast (avoids "welcome flood" on page open).
  const seenIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const current = new Set(rows.map((r) => r.reservationId));
    const prev = seenIdsRef.current;
    if (prev === null) {
      seenIdsRef.current = current;
      return;
    }
    const newOnes = rows.filter((r) => !prev.has(r.reservationId));
    if (newOnes.length > 0) {
      notifications.show({
        color: 'teal',
        title: `🔔 ${newOnes.length} 筆新訂位`,
        message: newOnes
          .map((r) => `${dayjs(r.startTime).tz(profile.timezone).format('MM/DD HH:mm')} · ${r.partySize} 位`)
          .join('\n'),
        autoClose: 8000,
      });
    }
    seenIdsRef.current = current;
  }, [rows, profile.timezone]);

  const [creating, setCreating] = useState(false);
  // First-visit onboarding tour — opens once per browser-localStorage
  // per key name bump (change 'v1' → 'v2' to force all users to re-see
  // an updated tour).
  const tourFlag = useFirstVisitFlag('dashboard-tour-v1');
  const [touring, setTouring] = useState(false);
  useEffect(() => {
    if (!tourFlag.seen) {
      setTouring(true);
    }
  }, [tourFlag.seen]);
  const [allocateTarget, setAllocateTarget] = useState<DashboardRow | null>(null);
  const [pricingZone, setPricingZone] = useState<Zone | null>(null);
  const [policyZone, setPolicyZone] = useState<Zone | null>(null);
  const [metadataZone, setMetadataZone] = useState<Zone | null>(null);
  const [unitsZone, setUnitsZone] = useState<Zone | null>(null);

  const actions = useReservationActions({ onChanged: reload });

  const customerLabels = useMemo(() => {
    const out = new Map<string, { label: string; emoji: string }>();
    rows.forEach((r, idx) => {
      const emoji = CARD_EMOJI[idx % CARD_EMOJI.length]!;
      out.set(r.reservationId, {
        label: String.fromCharCode(65 + (idx % 26)) + ' 組',
        emoji,
      });
    });
    return out;
  }, [rows]);

  const dateLabel = dayjs(now).tz(profile.timezone).format('YYYY-MM-DD ddd HH:mm');

  return (
    <Container size="xl">
      <Group justify="space-between" align="center" mb="md">
        <Alert
          color="gray"
          variant="light"
          icon={<IconLock size={16} />}
          radius="md"
          style={{ flex: 1 }}
        >
          僅限員工操作 · 客人自助訂位功能待後續開放
        </Alert>
        <Group gap="xs">
          <Button
            variant="subtle"
            color={tokens.color.mantine['primary']}
            leftSection={<IconCompass size={16} />}
            onClick={() => setTouring(true)}
          >
            體驗導覽
          </Button>
          <Tooltip label="匯出接下來 24 小時預約 CSV">
            <Button
              variant="subtle"
              color={tokens.color.mantine['primary']}
              leftSection={<IconDownload size={16} />}
              onClick={() => {
                const csv = rowsToCsv(rows, profile.timezone);
                const name = `reservations_${dayjs(now).format('YYYYMMDD_HHmm')}.csv`;
                downloadCsv(name, csv);
              }}
              disabled={rows.length === 0}
            >
              匯出 CSV
            </Button>
          </Tooltip>
          <Tooltip label="重新整理">
            <ActionIcon
              variant="light"
              color={tokens.color.mantine['primary']}
              onClick={reload}
              disabled={loading}
              size="lg"
              radius="xl"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <HelpPopover title={PAGE_HELP['dashboard']!.title} body={PAGE_HELP['dashboard']!.body} />
        </Group>
      </Group>

      <TodayStatsHero
        appTitle={profile.branding.appTitle}
        venueName={profile.name}
        tagline={profile.branding.tagline}
        dateLabel={dateLabel}
        rows={rows}
        zones={zones}
        unitLabel={profile.branding.tileUnitLabel}
      />

      {error && (
        <Text c="red" size="sm" mb="sm">
          {error}
        </Text>
      )}

      <Box mb="md">
        <TimelineView
          rows={rows}
          windowStartIso={windowStart}
          windowEndIso={windowEnd}
          timezone={profile.timezone}
          customerLabels={customerLabels}
        />
      </Box>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Group justify="space-between" align="center" mb="md">
            <Title order={4}>接下來 24 小時</Title>
            <Button
              color={tokens.color.mantine['primary']}
              radius="xl"
              leftSection={<IconCalendarPlus size={16} />}
              onClick={() => setCreating(true)}
            >
              新增預約
            </Button>
          </Group>
          {rows.length === 0 && !loading ? (
            <Box
              style={{
                padding: 40,
                background: tokens.color.cardBackground,
                borderRadius: tokens.radius.xl,
                border: tokens.color.dashedBorder,
                textAlign: 'center',
              }}
            >
              <Text size="xl" mb="xs">
                🌸
              </Text>
              <Text c="dimmed" size="sm">
                今天還沒有預約，點右上「新增預約」建立第一筆！
              </Text>
            </Box>
          ) : (
            <Stack gap="md">
              {rows.map((r) => {
                const info = customerLabels.get(r.reservationId) ?? {
                  label: '—',
                  emoji: '✨',
                };
                return (
                  <ReservationCard
                    key={r.reservationId}
                    reservation={r}
                    timezone={profile.timezone}
                    customerLabel={info.label}
                    emoji={info.emoji}
                    busy={actions.isBusy(r.reservationId)}
                    onAllocate={() => setAllocateTarget(r)}
                    onCheckIn={() => void actions.checkIn(r)}
                    onComplete={() => void actions.complete(r)}
                    onCancel={() => void actions.cancel(r)}
                    onNoShow={() => void actions.noShow(r)}
                  />
                );
              })}
            </Stack>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            <SeatMap
              zones={zones}
              loading={loading}
              unitLabel={profile.branding.tileUnitLabel}
              onEditPricing={setPricingZone}
              onEditPolicy={setPolicyZone}
              onEditMetadata={setMetadataZone}
              onEditUnits={setUnitsZone}
            />
            <NowPlayingPanel venueId={profile.venueId} />
            {profile.capabilities.opsConsole.showTakeoutPanel && (
              <TakeoutPanel venueId={profile.venueId} timezone={profile.timezone} />
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <NewReservationModal
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={() => reload()}
        venueId={profile.venueId}
        timezone={profile.timezone}
        zones={zoneSummaries}
      />
      <AllocateSeatsModal
        opened={allocateTarget !== null}
        onClose={() => setAllocateTarget(null)}
        onAllocated={() => reload()}
        reservation={allocateTarget}
        venueId={profile.venueId}
        zones={zoneSummaries}
      />
      <TourModal
        opened={touring}
        onClose={() => {
          setTouring(false);
          tourFlag.markSeen();
        }}
      />

      {pricingZone && (
        <ZonePricingModal
          opened={pricingZone !== null}
          onClose={() => setPricingZone(null)}
          zone={pricingZone}
          onSaved={() => reload()}
        />
      )}
      {policyZone && (
        <ServicePolicyModal
          opened={policyZone !== null}
          onClose={() => setPolicyZone(null)}
          zone={policyZone}
          venueId={profile.venueId}
          timezone={profile.timezone}
          onSaved={() => reload()}
        />
      )}
      {metadataZone && (
        <ZoneMetadataModal
          opened={metadataZone !== null}
          onClose={() => setMetadataZone(null)}
          zone={metadataZone}
          onSaved={() => reload()}
        />
      )}
      {unitsZone && (
        <ZoneUnitsModal
          opened={unitsZone !== null}
          onClose={() => setUnitsZone(null)}
          zone={unitsZone}
          unitLabel={profile.branding.tileUnitLabel}
          onSaved={() => reload()}
        />
      )}
    </Container>
  );
}
